import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Paper,
  Divider,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Download,
  Add as AddIcon,
  Delete as DeleteIcon,
  Chat,
  FolderOpen,
  Article,
} from '@mui/icons-material';
import { extractErrorMessage } from '../../../utils/errorUtils';
import documentService from '../services/documentService';
import AiChatDialog from '../../../components/AiChatDialog';
import { usePermissions } from '../../../hooks/usePermissions';

const MEDIUM_OPTIONS = [
  { value: 'digital', label: 'Digital' },
  { value: 'fisico', label: 'Físico' },
  { value: 'CD', label: 'CD' },
  { value: 'DVD', label: 'DVD' },
  { value: 'USB', label: 'USB' },
  { value: 'microfilm', label: 'Microfilm' },
  { value: 'microfichas', label: 'Microfichas' },
];

const getFileExt = (filename) => {
  if (!filename) return '';
  return filename.split('.').pop().toLowerCase();
};

const isPreviewable = (doc) => {
  return [doc.file_original_name, doc.name, doc.file]
    .filter(Boolean)
    .some((n) => ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'].includes(getFileExt(n)));
};

const isPdf = (doc) => {
  return [doc.file_original_name, doc.name, doc.file]
    .filter(Boolean)
    .some((n) => getFileExt(n) === 'pdf');
};

const isImage = (doc) => {
  return [doc.file_original_name, doc.name, doc.file]
    .filter(Boolean)
    .some((n) => ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(getFileExt(n)));
};

const DocumentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [chatOpen, setChatOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    documentDate: '',
    medium: 'digital',
    notes: '',
    meta: [],
  });

  useEffect(() => {
    loadDocument();
  }, [id]);

  const loadDocument = async () => {
    try {
      setLoading(true);
      const response = await documentService.getById(id);
      const data = response.data || response;
      setDoc(data);

      const metaArray = data.meta
        ? Array.isArray(data.meta)
          ? data.meta
          : Object.entries(data.meta).map(([key, value]) => ({ key, value }))
        : [];

      let formattedDate = '';
      if (data.documentDate) {
        try {
          const date = new Date(data.documentDate);
          if (!isNaN(date.getTime())) formattedDate = date.toISOString().split('T')[0];
        } catch (_) {}
      }

      setFormData({
        name: data.name || data.file_original_name || '',
        documentDate: formattedDate,
        medium: data.medium || 'digital',
        notes: data.notes || '',
        meta: metaArray,
      });
    } catch (err) {
      console.error('Error loading document:', err);
      showSnackbar('Error al cargar el documento', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddMeta = () => {
    setFormData((prev) => ({ ...prev, meta: [...prev.meta, { key: '', value: '' }] }));
  };

  const handleMetaChange = (index, field, value) => {
    setFormData((prev) => {
      const newMeta = [...prev.meta];
      newMeta[index][field] = value;
      return { ...prev, meta: newMeta };
    });
  };

  const handleRemoveMeta = (index) => {
    setFormData((prev) => ({ ...prev, meta: prev.meta.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showSnackbar('El nombre es requerido', 'error');
      return;
    }
    try {
      setSaving(true);
      const metaObject = formData.meta.reduce((acc, item) => {
        if (item.key.trim()) acc[item.key.trim()] = item.value;
        return acc;
      }, {});

      const payload = {
        name: formData.name.trim(),
        documentDate: formData.documentDate || null,
        medium: formData.medium,
        notes: formData.notes.trim() || null,
        meta: Object.keys(metaObject).length > 0 ? metaObject : null,
        companyId: parseInt(doc.companyId),
      };

      await documentService.update(id, payload);
      showSnackbar('Documento actualizado correctamente', 'success');
      // Refetch to get updated data
      await loadDocument();
    } catch (err) {
      showSnackbar(extractErrorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    if (doc?.url) {
      const a = document.createElement('a');
      a.href = doc.url;
      a.download = doc.name || doc.file_original_name || 'documento';
      a.click();
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!doc) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Documento no encontrado.</Typography>
      </Box>
    );
  }

  const canPreview = doc.url && isPreviewable(doc);
  const docIsImage = doc.url && isImage(doc);
  const displayName = doc.name || doc.file_original_name || 'Documento';

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" noWrap title={displayName}>
            {displayName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {doc.file && isPdf(doc) && hasPermission('ai.chat') && (
            <Button startIcon={<Chat />} variant="outlined" onClick={() => setChatOpen(true)}>
              Chatear
            </Button>
          )}
          {doc.url && (
            <Button startIcon={<Download />} variant="outlined" onClick={handleDownload}>
              Descargar
            </Button>
          )}
          <Button
            startIcon={<Save />}
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </Box>
      </Box>

      {/* Association chips */}
      {(doc.documentProceedings?.length > 0 || doc.correspondence_document?.length > 0) && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {doc.documentProceedings?.map((dp) => (
            <Chip
              key={`proc-${dp.proceeding.id}`}
              icon={<FolderOpen fontSize="small" />}
              label={`Expediente: ${dp.proceeding.code || dp.proceeding.name}`}
              color="primary"
              variant="outlined"
              clickable
              onClick={() => navigate(`/proceedings/${dp.proceeding.id}`)}
            />
          ))}
          {doc.correspondence_document?.map((cd) => (
            <Chip
              key={`corr-${cd.correspondences.id}`}
              icon={<Article fontSize="small" />}
              label={`Correspondencia: ${cd.correspondences.code || cd.correspondences.title}`}
              color="secondary"
              variant="outlined"
              clickable
              onClick={() => navigate(`/correspondences/${cd.correspondences.id}`)}
            />
          ))}
        </Box>
      )}

      {/* Split layout */}
      <Grid container spacing={3} sx={{ height: 'calc(100vh - 160px)' }}>
        {/* Left: Preview */}
        <Grid item xs={12} md={7} sx={{ height: '100%' }}>
          <Paper sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {canPreview ? (
              docIsImage ? (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#111', p: 2 }}>
                  <img
                    src={doc.url}
                    alt={displayName}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </Box>
              ) : (
                <iframe
                  src={doc.url}
                  title={displayName}
                  width="100%"
                  height="100%"
                  style={{ border: 'none', display: 'block', flex: 1 }}
                />
              )
            ) : (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, gap: 2 }}>
                <Typography variant="h6" color="text.secondary">
                  Vista previa no disponible
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  Este tipo de archivo no puede mostrarse en el navegador.
                </Typography>
                {doc.url && (
                  <Button startIcon={<Download />} variant="contained" onClick={handleDownload}>
                    Descargar archivo
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right: Edit form */}
        <Grid item xs={12} md={5} sx={{ height: '100%' }}>
          <Paper sx={{ p: 3, height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Editar documento
            </Typography>

            <TextField
              label="Nombre"
              fullWidth
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />

            <TextField
              label="Fecha inicial"
              type="date"
              fullWidth
              value={formData.documentDate}
              onChange={(e) => handleChange('documentDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              select
              label="Soporte"
              fullWidth
              value={formData.medium}
              onChange={(e) => handleChange('medium', e.target.value)}
            >
              {MEDIUM_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            <Divider />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">
                Información adicional
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddMeta}>
                Añadir fila
              </Button>
            </Box>

            {formData.meta.map((item, index) => (
              <Grid container spacing={1} key={index} alignItems="center">
                <Grid item xs={5}>
                  <TextField
                    label="Clave"
                    size="small"
                    fullWidth
                    value={item.key}
                    onChange={(e) => handleMetaChange(index, 'key', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Valor"
                    size="small"
                    fullWidth
                    value={item.value}
                    onChange={(e) => handleMetaChange(index, 'value', e.target.value)}
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton size="small" color="error" onClick={() => handleRemoveMeta(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Divider />

            <TextField
              label="Notas"
              fullWidth
              multiline
              rows={4}
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 'auto' }}>
              <Button onClick={() => navigate(-1)}>Cancelar</Button>
              <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <AiChatDialog
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        mode="document"
        entityId={doc.id}
        entityName={displayName}
      />
    </Box>
  );
};

export default DocumentDetail;
